import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronRight, SkipForward, ArrowLeft, History, ChevronDown, ShieldCheck, ShieldAlert, Shield } from "lucide-react";
import { cn } from "@/lib/utils";

interface PreviousAttemptsStepProps {
  initialValue: string;
  trigger?: string;
  onNext: (value: string) => void;
  onSkip: () => void;
  onBack: () => void;
}

const TEST_OPTIONS = [
  { id: "interviews", label: "Kundeninterviews geführt", weight: 2 },
  { id: "willingness", label: "Zahlungsbereitschaft getestet", weight: 3 },
  { id: "pilot", label: "Pilot angeboten", weight: 3 },
  { id: "built_unvalidated", label: "Feature gebaut ohne Marktvalidierung", weight: 0, warn: true },
  { id: "nothing", label: "Noch nichts getestet", weight: 0 },
];

const BLOCKER_CATEGORIES = [
  { id: "intern", label: "Intern", hint: "Ressourcen, Team, Fokus" },
  { id: "extern", label: "Extern", hint: "Markt, Kunden, Wettbewerb" },
];

function parseStored(raw: string): { checks: string[]; learning: string; signal: string; blocker: string; blockerType: string } {
  const defaults = { checks: [] as string[], learning: "", signal: "", blocker: "", blockerType: "" };
  if (!raw || !raw.startsWith("{")) return defaults;
  try { return { ...defaults, ...JSON.parse(raw) }; } catch { return defaults; }
}

function getValidationLevel(checks: string[], signal: string, blocker: string): { level: "none" | "low" | "medium" | "high"; label: string; icon: typeof Shield; color: string; percent: number; hint: string } {
  if (checks.includes("nothing")) return { level: "none", label: "Keine Validierung", icon: ShieldAlert, color: "text-destructive", percent: 5, hint: "Höheres Analyserisiko – die KI arbeitet mit Annahmen statt Daten." };
  
  let score = checks.reduce((s, id) => s + (TEST_OPTIONS.find(o => o.id === id)?.weight ?? 0), 0);
  if (signal.trim().length > 10) score += 2;
  if (blocker.trim().length > 10) score += 1;

  if (score >= 8) return { level: "high", label: "Hohe Validierung", icon: ShieldCheck, color: "text-emerald-500", percent: 100, hint: "Starke Datenbasis – die Analyse kann auf echten Signalen aufbauen." };
  if (score >= 5) return { level: "medium", label: "Mittlere Validierung", icon: Shield, color: "text-amber-500", percent: 60, hint: "Solide Grundlage – ein konkretes Marktsignal würde die Qualität steigern." };
  if (score >= 2) return { level: "low", label: "Niedrige Validierung", icon: ShieldAlert, color: "text-orange-500", percent: 30, hint: "Wenige Datenpunkte – mehr Tests würden die Entscheidungsqualität erhöhen." };
  return { level: "none", label: "Keine Validierung", icon: ShieldAlert, color: "text-destructive", percent: 5, hint: "Noch keine Eingaben – wähle mindestens einen Test aus." };
}

export const PreviousAttemptsStep = ({ initialValue, onNext, onSkip, onBack }: PreviousAttemptsStepProps) => {
  const initial = parseStored(initialValue);
  const [checks, setChecks] = useState<string[]>(initial.checks);
  const [learning, setLearning] = useState(initial.learning);
  const [signal, setSignal] = useState(initial.signal);
  const [blocker, setBlocker] = useState(initial.blocker);
  const [blockerType, setBlockerType] = useState(initial.blockerType);
  const [isExpanded, setIsExpanded] = useState(!!initialValue);

  const nothingSelected = checks.includes("nothing");
  const hasRealTests = checks.length > 0 && !nothingSelected;
  const validation = useMemo(() => getValidationLevel(checks, signal, blocker), [checks, signal, blocker]);

  // Signal is required if user has done real tests
  const signalRequired = hasRealTests;
  const canProceed = !isExpanded || !signalRequired || signal.trim().length > 0;

  // Progress bar color mapping
  const barColor = validation.level === "high" ? "bg-emerald-500" :
    validation.level === "medium" ? "bg-amber-500" :
    validation.level === "low" ? "bg-orange-500" : "bg-destructive";

  const toggleCheck = (id: string) => {
    if (id === "nothing") {
      setChecks(prev => prev.includes("nothing") ? [] : ["nothing"]);
    } else {
      setChecks(prev => {
        const without = prev.filter(c => c !== "nothing");
        return without.includes(id) ? without.filter(c => c !== id) : [...without, id];
      });
    }
  };

  const serialize = () => JSON.stringify({ checks, learning, signal, blocker, blockerType });
  const handleNext = () => onNext(serialize());

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canProceed) handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [checks, learning, signal, blocker, blockerType, canProceed]);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <History className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schritt 3 von 4</span>
            <span className="px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground">Optional</span>
          </div>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-foreground">
          Realitätsabgleich: Was wissen wir bereits?
        </h2>
        <p className="text-muted-foreground text-base">
          Keine Wiederholung – nur echte Tests, Signale und Blocker.
        </p>
      </div>

      {isExpanded && (
        <div className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-500",
          validation.level === "high" ? "border-emerald-500/30 bg-emerald-500/5" :
          validation.level === "medium" ? "border-amber-500/30 bg-amber-500/5" :
          validation.level === "low" ? "border-orange-500/30 bg-orange-500/5" :
          "border-destructive/30 bg-destructive/5"
        )}>
          <validation.icon className={cn("h-5 w-5 shrink-0", validation.color)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className={cn("text-sm font-semibold", validation.color)}>{validation.label}</span>
              <span className="text-xs text-muted-foreground">{validation.percent}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-muted/50 overflow-hidden">
              <div
                className={cn("h-full rounded-full transition-all duration-700 ease-out", barColor)}
                style={{ width: `${validation.percent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{validation.hint}</p>
          </div>
        </div>
      )}

      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full flex items-center justify-between p-4 rounded-xl border border-border/60 bg-card/50 hover:border-primary/40 hover:bg-card transition-all duration-300 text-left"
        >
          <span className="text-sm text-muted-foreground">Optional – spart Zeit, verbessert Qualität</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      ) : (
        <div className="space-y-6 animate-fade-in">

          {/* 1. Test-Realität */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">1. Test-Realität</h3>
            <div className="space-y-2.5">
              {TEST_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200",
                    checks.includes(opt.id)
                      ? opt.warn
                        ? "border-amber-500/50 bg-amber-500/5"
                        : "border-primary/50 bg-primary/5"
                      : "border-border/40 bg-card/30 hover:border-border/60",
                    opt.id !== "nothing" && nothingSelected && "opacity-40 pointer-events-none"
                  )}
                >
                  <Checkbox
                    checked={checks.includes(opt.id)}
                    onCheckedChange={() => toggleCheck(opt.id)}
                  />
                  <span className="text-sm text-foreground">{opt.label}</span>
                  {opt.warn && checks.includes(opt.id) && (
                    <span className="ml-auto text-xs text-amber-600">⚠ Risiko: keine Marktvalidierung</span>
                  )}
                </label>
              ))}
            </div>
            {hasRealTests && (
              <div className="pt-1 space-y-1.5 animate-fade-in">
                <Label className="text-xs text-muted-foreground">Wichtigstes Learning (1–2 Sätze)</Label>
                <Input
                  value={learning}
                  onChange={(e) => setLearning(e.target.value)}
                  placeholder="z. B. Kunden wollen Self-Service, nicht Enterprise-Sales"
                  className="bg-card/50 border-border/40"
                />
              </div>
            )}
          </div>

          {/* 2. Stärkstes Marktsignal */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">2. Stärkstes Marktsignal</h3>
              {signalRequired && <span className="text-xs text-destructive font-medium">Pflicht</span>}
            </div>
            <p className="text-xs text-muted-foreground">Was war das stärkste reale Signal – positiv oder negativ?</p>
            <Input
              value={signal}
              onChange={(e) => setSignal(e.target.value.slice(0, 150))}
              placeholder="z. B. 3 Enterprise-Leads haben nach On-Premise gefragt"
              className={cn(
                "bg-card/50 border-border/40",
                signalRequired && signal.trim().length === 0 && "border-destructive/40"
              )}
              maxLength={150}
            />
            <div className="text-right text-xs text-muted-foreground/60">{signal.length}/150</div>
          </div>

          {/* 3. Größter Blocker */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">3. Größter Blocker</h3>
            <div className="flex gap-2 mb-2">
              {BLOCKER_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setBlockerType(blockerType === cat.id ? "" : cat.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200",
                    blockerType === cat.id
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border/40 bg-card/30 text-muted-foreground hover:border-border/60"
                  )}
                >
                  {cat.label} <span className="text-muted-foreground/60 ml-1">({cat.hint})</span>
                </button>
              ))}
            </div>
            <Input
              value={blocker}
              onChange={(e) => setBlocker(e.target.value.slice(0, 100))}
              placeholder={blockerType === "intern" ? "z. B. Kein dediziertes Enterprise-Sales-Team" : blockerType === "extern" ? "z. B. Markt noch nicht reif für Self-Service" : "Was hat euch bisher konkret gestoppt?"}
              className="bg-card/50 border-border/40"
              maxLength={100}
            />
            <div className="text-right text-xs text-muted-foreground/60">{blocker.length}/100</div>
          </div>

          <div className="flex items-center justify-end text-xs text-muted-foreground/60">
            <span>⌘ + Enter zum Fortfahren</span>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} className="gap-2 h-12 px-6 rounded-xl border-border/60 hover:bg-card hover:border-primary/40">
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button variant="ghost" onClick={onSkip} className="gap-2 h-12 px-6 rounded-xl hover:bg-muted/50">
          <SkipForward className="h-4 w-4" />
          Überspringen
        </Button>
        <Button onClick={handleNext} disabled={!canProceed} className="flex-1 gap-2 h-12 rounded-xl">
          Weiter
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
